import {get} from "lodash";
import root from "window-or-global";

import log, {serializeError} from "@atg-shared/log";
import {currentTrackingSystem} from "@atg-shared/system";

type EventKey = string;
type Mappings = {
    revenue?: {path: string; factor?: number};
    value?: {path: string; factor?: number};
};

/**
 * Mapping to extract revenue and custom value field for conversion events in dataLayer to be sent to Optimizely.
 * The path is defined using lodash get() syntax.
 * For revenue and value, an optional factor can be provided to convert the value to minor currency units (e.g. from kronor -> cents).
 * If no event properties mapping is defined for an event, it will be tracked without any additional properties.
 *
 * Example:
 * {
 *   [eventKey]: {
 *     revenue: {
 *       path: "some.nested.path.to.revenue.field",
 *       factor: 100
 *     },
 *     value: {
 *       path: "some.nested.path.to.custom.value.field",
 *       factor: 1
 *     }
 *   }
 * }
 */
const eventPropertiesMapping: Record<EventKey, Mappings> = {
    purchase: {
        revenue: {
            path: "ecommerce.purchase.actionField.revenue",
            // Optimizely expects revenue in minor currency units and the purchase event provides it in major currency units
            factor: 100,
        },
    },
    Purchase: {
        revenue: {
            path: "ecommerce.purchase.actionField.revenue",
            // Optimizely expects revenue in minor currency units and the purchase event provides it in major currency units
            factor: 100,
        },
    },
    "sharedBet/SHARE_PRODUCT": {
        revenue: {
            path: "product_ownprice[0]",
            factor: 100,
        },
    },
    purchaseTillsammansShare: {
        revenue: {
            path: "cost",
            factor: 100,
        },
    },
    SHOP_ANALYTICS: {
        revenue: {
            path: "share_price",
            factor: 100,
        },
    },
    "kambi place bet": {
        revenue: {
            path: "kambi.ecommerce.purchase.actionField.revenue",
            factor: 100,
        },
    },
    kambiRandomised: {
        revenue: {
            path: "ecommerce_random.purchase.actionField.revenue",
            factor: 100,
        },
    },
    deposit: {
        revenue: {
            path: "amount",
            factor: 1,
        },
    },
};

function getMappedValues(event: Record<string, unknown>, eventMappings: Mappings) {
    const revenue = eventMappings.revenue
        ? Number(get(event, eventMappings.revenue.path))
        : undefined;
    const value = eventMappings.value
        ? Number(get(event, eventMappings.value.path))
        : undefined;

    return {
        revenue:
            revenue && !Number.isNaN(revenue)
                ? revenue * (eventMappings?.revenue?.factor ?? 1)
                : undefined,
        value:
            value && !Number.isNaN(value)
                ? value * (eventMappings?.value?.factor ?? 1)
                : undefined,
    };
}

function flattenArray(
    arr: unknown[],
    parentKey = "",
    result: Record<string, unknown> = {},
) {
    arr.forEach((item, index) => {
        const newKey = `${parentKey}[${index}]`;
        if (item && typeof item === "object" && !Array.isArray(item)) {
            flattenObject(item as Record<string, unknown>, newKey, result);
        } else if (Array.isArray(item)) {
            flattenArray(item, newKey, result);
        } else {
            result[newKey] = item;
        }
    });
    return result;
}

function flattenObject(
    obj: Record<string, unknown> = {},
    parentKey = "",
    result: Record<string, unknown> = {},
) {
    for (const [key, value] of Object.entries(obj)) {
        const newKey = parentKey ? `${parentKey}.${key}` : key;
        if (value && typeof value === "object" && !Array.isArray(value)) {
            flattenObject(value as Record<string, unknown>, newKey, result);
        } else if (Array.isArray(value)) {
            flattenArray(value, newKey, result);
        } else {
            result[newKey] = value;
        }
    }
    return result;
}

/**
 * Function that tracks events to optimizely when they match an event key
 */
export function trackOptimizelyEvent(gtmEvent: Record<string, any>) {
    const {optimizely} = root;
    if (!optimizely) {
        log.warn("Optimizely is not defined, cannot track event:", {
            gtmEvent,
        });
        return;
    }

    const optimizelyEvents = optimizely.getOptimizelyConfig()?.events || [];

    try {
        if (gtmEvent && typeof gtmEvent === "object" && "event" in gtmEvent) {
            const {event: eventKey, ...properties} = gtmEvent;

            if (
                gtmEvent._eventOrigin &&
                gtmEvent._eventOrigin !== currentTrackingSystem()
            ) {
                // only track events from the current environment to prevent duplicate tracking in Optimizely
                return;
            }

            // Only track events that are defined in the Optimizely datafile
            if (!optimizelyEvents.find((e) => e.key === eventKey)) return;
            const mappings = eventPropertiesMapping[eventKey];
            const {revenue, value} = mappings ? getMappedValues(gtmEvent, mappings) : {};

            root.optimizelyUserContext.trackEvent(eventKey, {
                revenue,
                value,
                $opt_event_properties: {
                    URL: root.location?.pathname,
                    ...flattenObject(properties),
                },
            });
        }
    } catch (error: unknown) {
        log.warn("Failed to track Optimizely event:", {
            error: serializeError(error),
            gtmEvent,
        });
    }
}
